/**
 * Lightweight Google Play rating invitation state.
 * Per-user, local-only preferences; no data is written to the matches table.
 * "Clicked" means the Play listing was opened, NOT that a review was submitted.
 */
export const PLAY_REVIEW_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;
export const PLAY_REVIEW_MIN_MATCHES = 5;
export const PLAY_REVIEW_MAX_LATER = 3;

/** Android app-shell eligibility. PWA installs qualify; ordinary Chrome tabs do not. */
export function isAndroidInstalledApp(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  if (!/android/i.test(navigator.userAgent)) return false;
  try {
    if (window.matchMedia("(display-mode: standalone)").matches ||
        window.matchMedia("(display-mode: fullscreen)").matches ||
        window.matchMedia("(display-mode: minimal-ui)").matches) return true;
  } catch { /* Fall back to a verified TWA referrer below. */ }
  return rememberPlayTwaSession();
}

const TWA_KEY = "tl-play-twa-session:v1";
const REVIEW_KEY_PREFIX = "tl-play-review:v1:";
const PLAY_PACKAGE_ID = "com.tennislog.app";
const RELATED_APP_TIMEOUT_MS = 4000;

export interface PlayReviewState {
  lastShownAt: number;
  laterCount: number;
  clickedAt: number;
}

const emptyState = (): PlayReviewState => ({ lastShownAt: 0, laterCount: 0, clickedAt: 0 });

function localStorageOrNull(): Storage | null {
  try { return typeof window !== "undefined" ? window.localStorage : null; }
  catch { return null; }
}

function sessionStorageOrNull(): Storage | null {
  try { return typeof window !== "undefined" ? window.sessionStorage : null; }
  catch { return null; }
}

function nonNegativeNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : 0;
}

/**
 * Only the genuine Tennis Log Android package referrer is accepted.
 * Web/PWA standalone modes deliberately do NOT qualify: an Android PWA
 * installed outside Play is not the Play Store distribution.
 * Persist the TWA signal in sessionStorage for SPA navigation/OAuth returns.
 */
export function isPlayTwaSession(
  userAgent: string,
  referrer: string,
  sessionStore: Pick<Storage, "getItem" | "setItem"> | null = sessionStorageOrNull(),
): boolean {
  if (!/android/i.test(userAgent)) return false;
  const fresh = /^android-app:\/\/com\.tennislog\.app\/?$/i.test(referrer.trim());
  if (fresh) {
    try { sessionStore?.setItem(TWA_KEY, "1"); } catch { /* private-mode storage */ }
    return true;
  }
  try { return sessionStore?.getItem(TWA_KEY) === "1"; }
  catch { return false; }
}

/**
 * A referrer-only check is too brittle: the Android TWA can return to the
 * site after an OAuth redirect or a new document navigation with an empty /
 * web referrer. Chrome supports getInstalledRelatedApps() for Bubblewrap
 * applications with Digital Asset Links and the matching web manifest entry.
 *
 * Requiring BOTH a standalone display and the verified Play package avoids
 * showing a Play review request to normal Android Chrome tabs and iOS.
 */
export interface RelatedPlatformApp {
  platform?: string;
  id?: string;
}

export function isVerifiedPlayStandalone(
  ua: string,
  standalone: boolean,
  apps: readonly RelatedPlatformApp[],
): boolean {
  return /android/i.test(ua) && standalone &&
    apps.some((app) => app.platform === "play" && app.id === PLAY_PACKAGE_ID);
}

type RelatedAppNavigator = Navigator & {
  getInstalledRelatedApps?: () => Promise<RelatedPlatformApp[]>;
};

/** Allows for a second verified signal when the initial referrer is missing. */
export async function detectPlayTwaSession(): Promise<boolean> {
  if (typeof navigator === "undefined" || typeof window === "undefined") return false;
  if (rememberPlayTwaSession()) return true;

  const ua = navigator.userAgent;
  if (!/android/i.test(ua)) return false;
  let standalone = false;
  try {
    standalone = window.matchMedia("(display-mode: standalone)").matches ||
      window.matchMedia("(display-mode: fullscreen)").matches;
  } catch { return false; }
  if (!standalone) return false;

  const browser = navigator as RelatedAppNavigator;
  if (typeof browser.getInstalledRelatedApps !== "function") return false;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<RelatedPlatformApp[]>((resolve) => {
      timeoutId = setTimeout(() => resolve([]), RELATED_APP_TIMEOUT_MS);
    });
    const installed = await Promise.race([browser.getInstalledRelatedApps(), timeout]);
    const verified = isVerifiedPlayStandalone(ua, standalone, installed);
    if (verified) {
      try { sessionStorageOrNull()?.setItem(TWA_KEY, "1"); } catch { /* optional cache */ }
    }
    return verified;
  } catch { return false; }
  finally { if (timeoutId !== undefined) clearTimeout(timeoutId); }
}

/** Call on initial document boot before a full-page OAuth round trip. */
export function rememberPlayTwaSession(): boolean {
  if (typeof navigator === "undefined" || typeof document === "undefined") return false;
  return isPlayTwaSession(navigator.userAgent, document.referrer);
}

export function readPlayReviewState(
  userId: string,
  store: Pick<Storage, "getItem" | "setItem"> | null = localStorageOrNull(),
): PlayReviewState | null {
  if (!userId || !store) return null;
  try {
    const raw = store.getItem(REVIEW_KEY_PREFIX + userId);
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw) as Partial<PlayReviewState> | null;
    return {
      lastShownAt: nonNegativeNumber(parsed?.lastShownAt),
      laterCount: Math.floor(nonNegativeNumber(parsed?.laterCount)),
      clickedAt: nonNegativeNumber(parsed?.clickedAt),
    };
  } catch {
    // Storage may be blocked; avoid prompts that cannot respect opt-outs.
    return null;
  }
}

function writeReviewState(
  userId: string,
  state: PlayReviewState,
  store: Pick<Storage, "getItem" | "setItem"> | null,
): boolean {
  if (!userId || !store) return false;
  try {
    store.setItem(REVIEW_KEY_PREFIX + userId, JSON.stringify(state));
    return true;
  } catch { return false; }
}

export type PlayReviewEligibilityStatus =
  | "eligible" | "signed_out" | "not_android_app" | "under_five"
  | "cooldown" | "opted_out" | "storage_unavailable";

/**
 * Deterministic reason code shared by the Dashboard and Support Diagnostics.
 * Does not send data anywhere and never reveals match/user details.
 */
export function getPlayReviewEligibilityStatus(
  userId: string | null | undefined,
  matchCount: number,
  isInstalledAndroid: boolean,
  now = Date.now(),
  store: Pick<Storage, "getItem" | "setItem"> | null = localStorageOrNull(),
): PlayReviewEligibilityStatus {
  if (!userId) return "signed_out";
  if (!isInstalledAndroid) return "not_android_app";
  if (!Number.isFinite(matchCount) || matchCount < PLAY_REVIEW_MIN_MATCHES) return "under_five";
  const state = readPlayReviewState(userId, store);
  if (!state) return "storage_unavailable";
  if (state.clickedAt > 0 || state.laterCount >= PLAY_REVIEW_MAX_LATER) return "opted_out";
  if (state.lastShownAt > 0 && now - state.lastShownAt < PLAY_REVIEW_COOLDOWN_MS) return "cooldown";
  return "eligible";
}

export function shouldOfferPlayReview(
  userId: string,
  matchCount: number,
  isInstalledAndroid: boolean,
  now = Date.now(),
  store: Pick<Storage, "getItem" | "setItem"> | null = localStorageOrNull(),
): boolean {
  return getPlayReviewEligibilityStatus(userId, matchCount, isInstalledAndroid, now, store) === "eligible";
}

export function recordPlayReviewShown(
  userId: string,
  now = Date.now(),
  store: Pick<Storage, "getItem" | "setItem"> | null = localStorageOrNull(),
): boolean {
  const state = readPlayReviewState(userId, store);
  return !!state && writeReviewState(userId, { ...state, lastShownAt: now }, store);
}

export function postponePlayReview(
  userId: string,
  now = Date.now(),
  store: Pick<Storage, "getItem" | "setItem"> | null = localStorageOrNull(),
): boolean {
  const state = readPlayReviewState(userId, store);
  return !!state && writeReviewState(userId, {
    ...state, lastShownAt: now, laterCount: state.laterCount + 1,
  }, store);
}

export function recordPlayReviewClick(
  userId: string,
  now = Date.now(),
  store: Pick<Storage, "getItem" | "setItem"> | null = localStorageOrNull(),
): boolean {
  const state = readPlayReviewState(userId, store);
  return !!state && writeReviewState(userId, { ...state, clickedAt: now }, store);
}

/** Explicit opt-out from all Google Play review prompts, without implying a review was posted. */
export function recordPlayReviewOptOut(
  userId: string,
  now = Date.now(),
  store: Pick<Storage, "getItem" | "setItem"> | null = localStorageOrNull(),
): boolean {
  const state = readPlayReviewState(userId, store);
  return !!state && writeReviewState(userId, {
    ...state, lastShownAt: now, laterCount: PLAY_REVIEW_MAX_LATER,
  }, store);
}
